import React, { useState, useEffect, useCallback } from 'react';
    import { motion } from 'framer-motion';
    import { Link } from 'react-router-dom';
    import { FiPlus, FiTrendingUp, FiUsers, FiEye, FiArrowRight, FiActivity, FiInbox, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
    import { Button } from '@/components/ui/Button';
    import { VersionBadge } from '@/components/ui/VersionBadge';
    import { formatCurrency } from '@/lib/utils';
    import { useAuth } from '@/contexts/AuthContext';
    import { useNotifications } from '@/hooks/useNotifications';
    import supabase from '@/supabase/supabase';
    import toast from 'react-hot-toast';

    export function BusinessDashboard() {
      const { user, profile } = useAuth();
      const { notifications } = useNotifications();
      const [stats, setStats] = useState({ 
        activeCampaigns: 0, 
        totalSpent: 0, 
        activeDrivers: 0, 
        totalReach: 0,
        actionRequiredCount: 0
      });
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(null);
      const [retryCount, setRetryCount] = useState(0);

      const loadDashboardData = useCallback(async () => {
        if (!user?.id) return;

        try {
          setLoading(true);
          setError(null);

          // 1. Get Business Profile
          const { data: bizProfile, error: bizError } = await supabase
            .from('business_profiles')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (bizError) throw bizError;

          // If profile is missing, it might still be generating in the background trigger
          if (!bizProfile) {
            if (retryCount < 5) {
              setTimeout(() => { setRetryCount(prev => prev + 1); }, 2000);
              return;
            } else {
              setError('Your business profile is still being initialized. Please refresh in a moment.');
              setLoading(false);
              return;
            }
          }

          // 2. Get Campaign Stats
          const { data: campaigns, error: campError } = await supabase
            .from('campaigns')
            .select('status, estimated_cost, assigned_cars, print_confirmation_status')
            .eq('business_id', bizProfile.id);

          if (campError) throw campError;

          if (campaigns) {
            const active = campaigns.filter(c => c.status === 'active');
            const actionRequiredCount = campaigns.filter(c => c.print_confirmation_status === 'awaiting_artwork').length;
            const totalSpent = active.reduce((sum, c) => sum + (Number(c.estimated_cost) || 0), 0);
            const activeDrivers = active.reduce((sum, c) => sum + (Number(c.assigned_cars) || 0), 0);
            const estimatedReach = activeDrivers * 50000;

            setStats({
              activeCampaigns: active.length,
              totalSpent,
              activeDrivers,
              totalReach: estimatedReach,
              actionRequiredCount
            });
          }

          setLoading(false);
        } catch (err) {
          console.error('Dashboard Load Error:', err);
          setError('Failed to load dashboard data. Check your connection.');
          setLoading(false);
        }
      }, [user, retryCount]);

      useEffect(() => {
        loadDashboardData();
      }, [loadDashboardData]);

      if (loading) {
        return (
          <div className="min-h-[400px] flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-slate-500 font-bold uppercase text-xs tracking-widest">
              {retryCount > 0 ? `Initializing Profile (Attempt ${retryCount})...` : 'Loading Intelligence...'}
            </p>
          </div>
        );
      }

      if (error) {
        return (
          <div className="min-h-[400px] flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 bg-rose-50 dark:bg-rose-900/20 rounded-full flex items-center justify-center mb-4">
              <FiAlertCircle className="w-8 h-8 text-rose-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Dashboard Unavailable</h2>
            <p className="text-slate-500 max-w-md mb-6">{error}</p>
            <Button onClick={() => { setRetryCount(0); loadDashboardData(); }}>
              <FiRefreshCw className="mr-2" /> Try Again
            </Button>
          </div>
        );
      }

      const statCards = [
        { label: 'Active Campaigns', value: stats.activeCampaigns, icon: FiTrendingUp, color: 'text-primary-500', bg: 'bg-primary-50 dark:bg-primary-900/20' },
        { label: 'Estimated Reach', value: stats.totalReach > 0 ? `${(stats.totalReach / 1000000).toFixed(1)}M` : '0', icon: FiEye, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
        { label: 'Active Drivers', value: stats.activeDrivers, icon: FiUsers, color: 'text-accent-500', bg: 'bg-accent-50 dark:bg-accent-900/20' },
      ];

      return (
        <div className="space-y-8 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-left">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Business Overview</h1>
                <VersionBadge />
              </div>
              <p className="text-slate-500 dark:text-slate-400 uppercase text-xs tracking-widest font-bold">
                Welcome, {profile?.full_name ? profile.full_name.split(' ')[0] : 'Partner'}
              </p>
            </div>
            <Link to="/dashboard/business/campaigns/new">
              <Button className="shrink-0 h-11 px-6 shadow-lg shadow-primary-500/20 font-bold uppercase text-xs tracking-widest">
                <FiPlus className="w-5 h-5 mr-2" /> Launch New Campaign
              </Button>
            </Link>
          </div>

          {stats.actionRequiredCount > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800 rounded-3xl p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 shadow-sm">
              <div className="p-3 bg-rose-100 dark:bg-rose-800 rounded-2xl text-rose-600 dark:text-rose-400 shrink-0">
                <FiAlertCircle className="w-8 h-8" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-base font-black text-rose-900 dark:text-rose-100 uppercase tracking-widest">Action Required: Artwork Revisions</h3>
                <p className="text-sm text-rose-700 dark:text-rose-300 mt-1">
                  You have {stats.actionRequiredCount} campaign(s) that require updated artwork files before printing can begin. The print shop has requested adjustments.
                </p>
              </div>
              <Link to="/dashboard/business/campaigns?tab=revisions" className="w-full sm:w-auto mt-4 sm:mt-0">
                <Button className="w-full sm:w-auto h-12 bg-rose-500 hover:bg-rose-600 shadow-rose-500/20 text-white font-bold">
                  View Requested Files <FiArrowRight className="ml-2" />
                </Button>
              </Link>
            </motion.div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {statCards.map((stat, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: i * 0.1 }}
                className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{stat.label}</p>
                    <p className="text-3xl font-black text-slate-900 dark:text-white mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-14 h-14 rounded-2xl ${stat.bg} ${stat.color} flex items-center justify-center shadow-inner`}>
                    <stat.icon className="w-7 h-7" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8 text-left">
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-8 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Recent Activity</h3>
                <Link to="/notifications" className="text-sm font-bold text-primary-500 hover:text-primary-600 flex items-center">
                  View All <FiArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>
              
              <div className="space-y-6">
                {notifications.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300"><FiInbox /></div>
                    <p className="text-sm text-slate-400 font-medium">No recent activity to show.</p>
                  </div>
                ) : (
                  notifications.slice(0, 3).map((notif, i) => (
                    <div key={i} className="flex gap-4 items-start pb-6 border-b dark:border-slate-800 last:border-0 last:pb-0">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 text-primary-500"><FiActivity /></div>
                      <div>
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{notif.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{new Date(notif.created_at).toLocaleDateString()} · {notif.message}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="bg-slate-900 rounded-3xl p-8 text-white relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/20 rounded-full blur-3xl opacity-50" />
              <div className="relative z-10">
                <h3 className="text-xl font-black mb-2">Campaign Budget</h3>
                <p className="text-slate-400 text-sm mb-8">Summary of your active spending in LKR</p>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <span className="text-slate-400 text-sm">Spent this month</span>
                    <span className="text-2xl font-black">{formatCurrency(stats.totalSpent)}</span>
                  </div>
                  <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                    <div className="bg-primary-500 h-full transition-all duration-1000" style={{ width: stats.totalSpent > 0 ? '100%' : '0%' }} />
                  </div>
                </div>

                <Link to="/dashboard/business/campaigns">
                  <Button variant="outline" className="w-full mt-8 border-white/20 text-white hover:bg-white/10">
                    Manage Campaigns
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }